import React, { useEffect, useState } from "react";
import {
  parseImpersonationHash,
  saveImpersonationSession,
} from "./supportModeSession";
import {
  clearPersistedUserInfo,
  persistImpersonationUserInfo,
} from "./impersonationIdentity";
import { resolveSupportModeLandingPath } from "./supportModeLanding";
import { resolveImpersonationSupportTicketId } from "./supportCaseDeepLink";
import { CLIENT_SUPPORT_HUB_PATH } from "../supportHubPaths";
import { persistActiveMasterAccountId } from "../tenant/activeMasterAccount";
import { clearLegacyMockManagedBrands, clearPendingAuthorizationBrands } from "../lib/brandManagerStore";

/**
 * Entry point for SA Support Mode handoff.
 * SA opens /support-mode#kk_impersonation=<urlencoded JSON>
 * Then a hard navigation remounts the app with the session
 * (avoids SPA soft-nav keeping a stale Brand Manager bundle/state).
 *
 * When Impersonate into Case supplies targetPath / openSupportTicketId,
 * land directly in Support with that canonical conversation.
 * Otherwise, when a Brand was selected, land in Brand locations; else /app.
 */
export default function SupportModeBootstrap() {
  const [error, setError] = useState(null);

  useEffect(() => {
    const session = parseImpersonationHash(window.location.hash);
    if (!session?.token || !session?.ticketId) {
      setError("Missing or invalid Support Mode handoff payload.");
      return;
    }
    // Drop pre-#23 mock Brand Manager rows so the first paint cannot
    // flash Dunkin/Walmart Franchisee cards from localStorage.
    clearLegacyMockManagedBrands();
    clearPendingAuthorizationBrands();
    // Isolate impersonated identity from any prior MA login / demo profile.
    clearPersistedUserInfo();
    persistImpersonationUserInfo(session);
    if (session.masterAccountId) {
      persistActiveMasterAccountId(session.masterAccountId);
    }

    const brandId = session.brandId ? String(session.brandId).trim() : "";
    const brandName = session.brandName
      ? String(session.brandName).trim()
      : "";
    if (brandId && brandName) {
      try {
        localStorage.setItem(
          "currentBrand",
          JSON.stringify({
            name: brandName,
            type: session.brandRoleScope || null,
            id: brandId,
          }),
        );
      } catch (err) {
        console.warn("Failed to persist Support Mode brand context", err);
      }
    } else {
      try {
        localStorage.removeItem("currentBrand");
      } catch {
        /* ignore */
      }
    }

    // Normalize deep-link fields onto the session before save so Support can
    // always resolve the case from supportTicketId / openSupportTicketId.
    const openTicketId = resolveImpersonationSupportTicketId(session);
    if (openTicketId) {
      session.openSupportTicketId = openTicketId;
      session.supportTicketId = session.supportTicketId || openTicketId;
    }

    saveImpersonationSession(session);
    // Full document load so Support Mode banner + tenant bootstrap + Brand
    // Manager all mount against the saved session (not a soft React navigate).
    let landing = resolveSupportModeLandingPath(session);
    if (
      openTicketId &&
      (landing === CLIENT_SUPPORT_HUB_PATH ||
        landing.startsWith(`${CLIENT_SUPPORT_HUB_PATH}?`) ||
        landing === "/master-account/support" ||
        landing.startsWith("/master-account/support?"))
    ) {
      const sep = landing.includes("?") ? "&" : "?";
      landing = `${landing}${sep}openTicket=${encodeURIComponent(openTicketId)}`;
    }
    window.location.replace(landing);
  }, []);

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Support Mode</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      Starting Support Mode…
    </div>
  );
}
